import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TourService, TourStep } from './tour.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageDropdownComponent } from '../shared/components/language-dropdown/language-dropdown.component';
import { CoachPosition, positionTourCoach, TourBox } from './tour-coach-position';
import { TourRevealDirective } from './tour-reveal.directive';
import { revealTourTarget, visibleTourTarget } from './tour-target-visibility';

@Component({ selector: 'app-tour-overlay', standalone: true, imports: [CommonModule, TranslateModule, LanguageDropdownComponent, TourRevealDirective], templateUrl: './tour-overlay.component.html', styleUrls: ['./tour-overlay.component.scss'] })
export class TourOverlayComponent implements AfterViewInit, OnDestroy {
  rect: { top: number; left: number; width: number; height: number } | null = null;
  cardLeft = false;
  targetVisible = false;
  @ViewChild('coach') coach?: ElementRef<HTMLElement>;
  coachPosition: CoachPosition | null = null;
  private viewportWidth = 0;
  private viewportHeight = 0;
  private focusTarget = false;
  private resizeObserver?: ResizeObserver;
  private observer?: MutationObserver;
  private frame = 0;
  private revealedStep: TourStep | null = null;
  private revealedElement?: HTMLElement;
  private lastStep: TourStep | null = null;
  private subscriptions = new Subscription();
  constructor(public tour: TourService, public router: Router, private zone: NgZone, public translate: TranslateService) {}
  get visible(): boolean { return this.tour.active && !this.tour.reading && this.router.url.split(/[?#]/)[0] === this.tour.routeFor(this.tour.step); }
  get currentCopy() {
    const prefix = this.tour.step.toUpperCase() + (['menu', 'promos'].includes(this.tour.step) && this.tour.state?.completed ? '_DONE' : '');
    return { title: `TOUR.${prefix}_TITLE`, text: `TOUR.${prefix}_BODY` };
  }
  get revealKey(): string { return `${this.tour.step}:${!!this.tour.state?.completed}:${!!this.tour.state?.missing}`; }
  get progress(): string {
    return `TOUR.${this.tour.chapter === 4 ? this.tour.step.toUpperCase() : 'LOCATION'}_PROGRESS`;
  }
  get missingText(): string {
    if (this.tour.message$.value) return 'TOUR.MISSING_LOAD';
    return `TOUR.MISSING_${['promos', 'list', 'photos'].includes(this.tour.step) ? this.tour.step.toUpperCase() : 'MENU'}`;
  }
  ngAfterViewInit(): void {
    this.subscriptions.add(this.tour.state$.subscribe(state => {
      if (state?.step !== this.lastStep) {
        this.revealedStep = null; this.rect = null; this.lastStep = state?.step || null;
        this.coachPosition = null;
      }
      this.schedule();
    }));
    this.subscriptions.add(this.router.events.subscribe(() => this.schedule()));
    this.subscriptions.add(this.translate.onLangChange.subscribe(() => { this.focusTarget = true; this.schedule(); }));
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(records => {
        if (records.some(record => !(record.target as Element).closest?.('app-tour-overlay'))) this.schedule();
      });
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style', 'data-tour', 'data-tour-business-id'] });
      this.resizeObserver = new ResizeObserver(() => { this.focusTarget = true; this.schedule(); });
      window.addEventListener('resize', this.schedule, { passive: true });
      document.addEventListener('scroll', this.schedule, { capture: true, passive: true });
      document.addEventListener('load', this.schedule, true);
      document.addEventListener('error', this.schedule, true);
    });
  }
  private selector(): string {
    if (this.tour.step === 'location') return '[data-tour="location-control"]';
    if (this.tour.step === 'list') return `[data-tour="restaurant-card"][data-tour-business-id="${this.tour.restaurantId}"]`;
    if (this.tour.step === 'menu' && this.tour.state?.completed && document.querySelector('[data-tour="menu-detail"]')) return '[data-tour="menu-detail"]';
    return `[data-tour="${this.tour.step === 'photos' ? 'photo-ribbon' : this.tour.step === 'menu' ? 'menu-item' : 'promotions'}"]`;
  }
  readonly schedule = (): void => {
    if (this.frame) return;
    this.zone.runOutsideAngular(() => { this.frame = requestAnimationFrame(() => { this.frame = 0; this.measure(); }); });
  };
  private measure(): void {
    if (!this.visible) { if (this.rect) this.zone.run(() => { this.rect = null; }); return; }
    if (this.viewportWidth !== window.innerWidth || this.viewportHeight !== window.innerHeight) {
      this.focusTarget = true; this.viewportWidth = window.innerWidth; this.viewportHeight = window.innerHeight;
    }
    if (this.coach) this.resizeObserver?.observe(this.coach.nativeElement);
    const mobile = window.innerWidth <= 760;
    // Reveal the section before waiting for decoded targets; offscreen lazy images need this first scroll.
    if (this.tour.step === 'menu' && this.revealedStep !== 'menu') {
      document.querySelector<HTMLElement>('[data-tour="menu-section"]')?.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
    }
    const targets = Array.from(document.querySelectorAll<HTMLElement>(this.selector()));
    const target = targets.find(element => {
      const image = this.tour.step === 'photos'
        ? Array.from(element.querySelectorAll<HTMLImageElement>('img')).find(image => image.complete && image.naturalWidth > 0)
        : this.tour.step === 'menu'
          ? element.querySelector<HTMLImageElement>('app-menu-section img.food-preview, .item-img, .item-img-mobile')
          : null;
      if ((this.tour.step === 'photos' || this.tour.step === 'menu') && (!image?.complete || image.naturalWidth === 0)) return false;
      const box = element.getBoundingClientRect();
      return box.width > 5 && box.height > 5 && getComputedStyle(element).visibility !== 'hidden' && element.getClientRects().length > 0;
    });
    if (!target) {
      const position = this.positionCoach();
      if (this.rect || this.targetVisible || JSON.stringify(position) !== JSON.stringify(this.coachPosition)) {
        this.zone.run(() => { this.rect = null; this.targetVisible = false; this.coachPosition = position; });
      }
      return;
    }
    const readingMenu = this.tour.step === 'menu' && this.tour.state?.completed && target.dataset['tour'] === 'menu-detail';
    const newTarget = this.revealedStep !== this.tour.step || this.revealedElement !== target;
    const guide = this.coach?.nativeElement.getBoundingClientRect();
    const guideOnTop = this.tour.step === 'location' || (this.tour.step === 'promos' && !this.tour.state?.completed)
      || (this.tour.step === 'list' && window.innerHeight <= 740);
    const space = mobile && guideOnTop
      ? { top: (guide?.bottom ?? 94) + 20, bottom: window.innerHeight - 12 }
      : { top: 94, bottom: mobile ? (guide?.top ?? window.innerHeight * .62) - 20 : window.innerHeight };
    const before = target.getBoundingClientRect();
    const needsReveal = before.top < space.top || before.bottom > space.bottom;
    if (mobile && (newTarget || this.focusTarget) && needsReveal && this.tour.step !== 'location') {
      if (!this.coach) { this.schedule(); return; }
      revealTourTarget(target, space);
    } else if (!mobile && newTarget) {
      this.revealedStep = this.tour.step;
      this.revealedElement = target;
      if (this.tour.step === 'menu') {
        // Keep the menu heading in view; the guide now has its own reserved left column.
        const section = readingMenu ? target : document.querySelector<HTMLElement>('[data-tour="menu-section"]') || target;
        section.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start', inline: 'nearest' });
      } else if (this.tour.step === 'photos' && window.innerWidth >= 1100) {
        document.querySelector<HTMLElement>('[data-tour="photo-stage"]')?.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
      } else if (this.tour.step !== 'location') target.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'center', inline: 'nearest' });
    }
    this.revealedStep = this.tour.step; this.revealedElement = target; this.focusTarget = false;
    const box = target.getBoundingClientRect();
    const visibleBox = visibleTourTarget(target, { ...space, top: mobile ? space.top : 0, left: 6, right: window.innerWidth - 6 });
    const top = Math.max(mobile ? space.top - 6 : 0, (visibleBox?.top ?? 0) - 6), left = Math.max(6, (visibleBox?.left ?? 0) - 6);
    const rect = { top, left, width: Math.max(0, Math.min(window.innerWidth - left - 6, (visibleBox?.width ?? 0) + 12)), height: Math.max(0, Math.min(space.bottom - top, (visibleBox?.top ?? 0) + (visibleBox?.height ?? 0) + 6 - top)) };
    const visible = !!visibleBox;
    const cardLeft = window.innerWidth > 760 && box.left > window.innerWidth / 2;
    const position = this.positionCoach(box);
    const spotlight = this.tour.step === 'promos' && this.tour.state?.completed ? null : visible ? rect : null;
    if (JSON.stringify(spotlight) !== JSON.stringify(this.rect) || JSON.stringify(position) !== JSON.stringify(this.coachPosition) || cardLeft !== this.cardLeft || visible !== this.targetVisible) {
      this.zone.run(() => { this.rect = spotlight; this.coachPosition = position; this.targetVisible = visible; this.cardLeft = cardLeft; });
    }
  }
  private positionCoach(target?: TourBox): CoachPosition | null {
    if (window.innerWidth <= 760 || !this.coach || !['photos', 'menu', 'promos'].includes(this.tour.step)) return null;
    const stage = window.innerWidth >= 1100 ? document.querySelector<HTMLElement>(
      this.tour.step === 'photos' ? '[data-tour="photo-stage"]' : this.tour.step === 'menu' ? '[data-tour="menu-section"]' : '[data-tour="promotions"]') : null;
    const anchor = stage?.getBoundingClientRect() || target;
    if (!anchor || anchor.width <= 5 || anchor.height <= 5) return null;
    return positionTourCoach(anchor, { width: window.innerWidth, height: window.innerHeight },
      this.coach.nativeElement.getBoundingClientRect(), this.tour.step === 'photos' ? 'right' : 'left');
  }
  showTarget(): void { this.revealedStep = null; this.schedule(); }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe(); this.observer?.disconnect(); this.resizeObserver?.disconnect(); cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.schedule); document.removeEventListener('scroll', this.schedule, true);
    document.removeEventListener('load', this.schedule, true); document.removeEventListener('error', this.schedule, true);
  }
}
